import { FC, useState } from 'react';
import clsx from 'clsx';
import { BilledEntityItem } from 'src/use-cases/settings/useBilledEntities';
import { EntityItem } from './EntityItem';

interface EntityItemWithSubEntitiesProps {
  entity: BilledEntityItem;
  depth: number; // 0 for root, 1 for first sub-level, etc.
  parentPath: number[]; // Array of parent IDs representing the path to this entity
  onToggle: (path: number[]) => void; // Single toggle handler with path array
  calculateSelectedCount: (entity: BilledEntityItem) => number;
}

export const EntityItemWithSubEntities: FC<EntityItemWithSubEntitiesProps> = ({ entity, depth, parentPath, onToggle, calculateSelectedCount }) => {
  // Track expanded state for this entity
  const [expanded, setExpanded] = useState(true);

  // Build the full path to this entity
  const currentPath = [...parentPath, entity.id];

  // Handle toggle - pass the full path to the handler
  const handleToggle = () => {
    onToggle(currentPath);
  };

  // Determine indentation and border styling based on depth
  const getContainerClasses = () => {
    if (depth === 0) {
      return 'flex flex-col relative';
    }

    return 'relative';
  };

  return (
    <div className={clsx('relative flex flex-col', depth !== 0 && 'pl-[28px] pt-4')}>
      <EntityItem entity={entity} depth={depth} onToggle={handleToggle} expanded={expanded} onExpandToggle={() => setExpanded(!expanded)} />

      {/* Render sub-entities recursively if they exist */}
      {entity.subEntities && entity.subEntities.length > 0 && expanded && (
        <div className={getContainerClasses()}>
          {entity.subEntities.map((subEntity) => (
            <>
              <EntityItemWithSubEntities
                key={subEntity.id}
                entity={subEntity}
                depth={depth + 1}
                parentPath={currentPath}
                onToggle={onToggle}
                calculateSelectedCount={calculateSelectedCount}
              />
              <div className='absolute top-16 bottom-8 left-16 w-1 bg-neutral-20' />
            </>
          ))}
        </div>
      )}

      {expanded && entity?.subEntities?.length > 0 && <div className='absolute top-[42px] bottom-8 left-16 w-1 bg-neutral-20' />}
    </div>
  );
};
